import { z } from "zod";

type Brand<T, Name extends string> = T & { readonly __brand: Name };

/** Opaque database identifier. The application does not define its external format. */
export type SchoolId = Brand<string, "SchoolId">;
export type FormId = Brand<string, "FormId">;
/** Opaque submission identifier from legacy or current storage. */
export type SubmissionId = Brand<string, "SubmissionId">;
/** Opaque authentication-provider identifier. The application does not define its format. */
export type UserId = Brand<string, "UserId">;
export type EmailAddress = Brand<string, "EmailAddress">;
export type ISODateString = Brand<string, "ISODateString">;

/** Constructs an opaque school ID, whose only application-level invariant is non-emptiness. */
export function asSchoolId(value: string): SchoolId {
  if (!value) throw new Error("Invalid school id");
  return value as SchoolId;
}

export function asFormId(value: string): FormId {
  if (!/^[a-z0-9]{6}$/i.test(value)) throw new Error(`Invalid form id: ${value}`);
  return value.toLowerCase() as FormId;
}

export function asSubmissionId(value: string): SubmissionId {
  if (!/^[0-9A-Za-z_-]{15,64}$/.test(value)) {
    throw new Error(`Invalid submission id: ${value}`);
  }
  return value as SubmissionId;
}

/** Constructs an opaque user ID, whose only application-level invariant is non-emptiness. */
export function asUserId(value: string): UserId {
  if (!value) throw new Error("Invalid user id");
  return value as UserId;
}

export function asEmailAddress(value: string): EmailAddress {
  const result = z.string().trim().toLowerCase().pipe(z.email()).safeParse(value);
  if (!result.success) {
    throw new Error(`Invalid email address: ${value}`);
  }
  return result.data as EmailAddress;
}

export function asISODateString(value: string): ISODateString {
  const result = z.union([z.iso.date(), z.iso.datetime()]).safeParse(value);
  if (!result.success) {
    throw new Error(`Invalid ISO date string: ${value}`);
  }
  return result.data as ISODateString;
}

interface FormElementBase {
  id: string;
  label?: string;
  sublabel?: string;
  required?: boolean;
}

export interface FormOption {
  label?: string;
  sublabel?: string;
  content?: FormElement[];
}

export interface StaticFormElement extends FormElementBase {
  type: "paragraph" | "hr";
  content?: never;
}

export interface InputFormElement extends FormElementBase {
  type:
    | "signature"
    | "name-input"
    | "number-input"
    | "email-input"
    | "phone-input"
    | "short-text-input"
    | "long-text-input"
    | "date-input"
    | "datetime-input"
    | "time-input";
  content?: never;
}

export interface ChoiceFormElement extends FormElementBase {
  type: "dropdown" | "multiple-choice" | "radio";
  content: FormOption[];
}

export type FormElement = StaticFormElement | InputFormElement | ChoiceFormElement;
export type FormElementType = FormElement["type"];

export interface SignaturePoint {
  x: number;
  y: number;
}

export type SignatureValue = SignaturePoint[][];
export type SubmissionValue = string | string[] | SignatureValue;
export type SubmissionData = Record<string, SubmissionValue>;

export interface Submission {
  date: ISODateString;
  data: SubmissionData;
  confirmationMail?: EmailAddress | null;
}

export interface SchoolForm {
  id: FormId;
  schule: SchoolId;
  schulname: string;
  name: string;
  content: FormElement[];
  deadline?: ISODateString;
  users?: EmailAddress[];
  abgaben?: Record<SubmissionId, Submission>;
}

export interface SchoolFormSummary {
  id: FormId;
  schule: SchoolId;
  schulname: string;
  name: string;
  deadline?: ISODateString;
  numberOfSubmissions: number;
}

export interface StoredForm extends Omit<SchoolForm, "id" | "schule" | "schulname" | "content"> {
  id?: never;
  schule?: never;
  schulname?: never;
  content?: FormElement[];
}

export interface School {
  schule: SchoolId;
  schulname: string;
  domains: string[];
  forms: Record<FormId, StoredForm>;
}

export interface User {
  uid: UserId;
  email: EmailAddress;
  displayName?: string;
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function optionalString(record: UnknownRecord, key: string): string | undefined {
  const value = record[key];
  if (value === undefined) return undefined;
  if (typeof value !== "string") throw new Error(`Expected ${key} to be a string`);
  return value;
}

function optionalBoolean(record: UnknownRecord, key: string): boolean | undefined {
  const value = record[key];
  if (value === undefined) return undefined;
  if (typeof value !== "boolean") throw new Error(`Expected ${key} to be a boolean`);
  return value;
}

function requiredString(record: UnknownRecord, key: string): string {
  const value = optionalString(record, key);
  if (value === undefined) throw new Error(`Missing string: ${key}`);
  return value;
}

function parseFormOption(value: unknown): FormOption {
  if (!isRecord(value)) throw new Error("Invalid form option");
  const option: FormOption = {};
  const label = optionalString(value, "label");
  const sublabel = optionalString(value, "sublabel");
  if (label !== undefined) option.label = label;
  if (sublabel !== undefined) option.sublabel = sublabel;
  if (value.content !== undefined) option.content = parseFormElements(value.content);
  return option;
}

export function parseFormElement(value: unknown): FormElement {
  if (!isRecord(value)) throw new Error("Invalid form element");
  const id = requiredString(value, "id");
  const type = requiredString(value, "type");
  const common = {
    id,
    ...(optionalString(value, "label") !== undefined
      ? { label: optionalString(value, "label") }
      : {}),
    ...(optionalString(value, "sublabel") !== undefined
      ? { sublabel: optionalString(value, "sublabel") }
      : {}),
    ...(optionalBoolean(value, "required") !== undefined
      ? { required: optionalBoolean(value, "required") }
      : {}),
  };

  if (type === "paragraph" || type === "hr") return { ...common, type };
  if (
    type === "signature" ||
    type === "name-input" ||
    type === "number-input" ||
    type === "email-input" ||
    type === "phone-input" ||
    type === "short-text-input" ||
    type === "long-text-input" ||
    type === "date-input" ||
    type === "datetime-input" ||
    type === "time-input"
  ) {
    return { ...common, type };
  }
  if (type === "dropdown" || type === "multiple-choice" || type === "radio") {
    if (!Array.isArray(value.content)) throw new Error("Invalid choice content");
    return {
      ...common,
      type,
      content: value.content.map(parseFormOption),
    };
  }
  throw new Error(`Unknown form element type: ${type}`);
}

export function parseFormElements(value: unknown): FormElement[] {
  if (!Array.isArray(value)) throw new Error("Invalid form content");
  return value.map(parseFormElement);
}

export function parseSubmissionValue(value: unknown): SubmissionValue {
  if (typeof value === "string") return value;
  if (!Array.isArray(value)) throw new Error("Invalid submission value");
  if (value.every(item => typeof item === "string")) return value;
  if (
    value.every(
      line =>
        Array.isArray(line) &&
        line.every(
          point => isRecord(point) && typeof point.x === "number" && typeof point.y === "number"
        )
    )
  ) {
    return value.map(line => {
      if (!Array.isArray(line)) throw new Error("Invalid signature line");
      return line.map(point => {
        if (!isRecord(point) || typeof point.x !== "number" || typeof point.y !== "number") {
          throw new Error("Invalid signature point");
        }
        return { x: point.x, y: point.y };
      });
    });
  }
  throw new Error("Invalid submission value");
}

export function parseSubmissionData(value: unknown): SubmissionData {
  if (!isRecord(value)) throw new Error("Invalid submission data");
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, parseSubmissionValue(item)])
  );
}

export function parseSubmission(value: unknown): Submission {
  if (!isRecord(value)) throw new Error("Invalid submission");
  const confirmationMail = value.confirmationMail;
  if (
    confirmationMail !== undefined &&
    confirmationMail !== null &&
    typeof confirmationMail !== "string"
  ) {
    throw new Error("Invalid confirmation email");
  }
  return {
    date: asISODateString(requiredString(value, "date")),
    data: parseSubmissionData(value.data),
    ...(confirmationMail === undefined
      ? {}
      : {
          confirmationMail: confirmationMail === null ? null : asEmailAddress(confirmationMail),
        }),
  };
}

export function parseSubmissions(value: unknown): Record<SubmissionId, Submission> {
  if (!isRecord(value)) throw new Error("Invalid submissions");
  return Object.fromEntries(
    Object.entries(value).map(([id, submission]) => [
      asSubmissionId(id),
      parseSubmission(submission),
    ])
  );
}
